// src/layers/imagery/formatImageryDate.js
//
// Display formatting for "YYYY_MM" imagery acquisition dates, with an
// OPTIONAL day: "2025_06" → "Jun 2025", "2025_12_13" → "13 Dec 2025". Falls
// back to the raw string for anything that doesn't match either shape.
//
// Lives next to the imagery data model because the "YYYY_MM" convention is
// domain-specific — it's the acquisition_date format produced by
// groupFootprintsIntoSeries and consumed across the imagery UI.

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function formatAcquisitionDate(d) {
  // "2025_06" -> "Jun 2025";  "2025_12_13" -> "13 Dec 2025".
  //
  // THE DAY IS OPTIONAL BECAUSE THE DATASET KEY'S IS. parseDatasetKey accepts
  // YYYY_MM with an optional _DD, so a collect can now be identified to the day
  // — which is what allows two collects in one month. This has to follow, or a
  // day-precision date falls through to the raw-string branch below and every
  // caller renders "2025_12_13" verbatim: the time strip (exploreRows.js), SeriesRow,
  // ActiveLayers and MapView all format through here.
  //
  // The raw-string fallback is kept deliberately. It is what makes a malformed
  // or unexpected acquisition_date show up as itself rather than as a wrong
  // date, and it is why the day-precision case was legible rather than silent
  // when it first appeared.
  const m = /^(\d{4})_(\d{2})(?:_(\d{2}))?$/.exec(d ?? "");
  if (!m) return d ?? "";

  const month = MONTHS[parseInt(m[2], 10) - 1] ?? m[2];
  return m[3]
    ? `${parseInt(m[3], 10)} ${month} ${m[1]}`
    : `${month} ${m[1]}`;
}

/**
 * A Sentinel-2 annual composite's label (D2): acquisition_date YYYY_04_01 is
 * "Spring YYYY". Anything else is not a composite date and keeps the ordinary
 * format, rather than being given a season it does not have.
 * @param {string|null|undefined} d
 */
export function formatReferenceDate(d) {
  const m = /^(\d{4})_04_01$/.exec(d ?? "");
  return m ? `Spring ${m[1]}` : formatAcquisitionDate(d);
}

/**
 * A Sentinel-2 annual composite's date range, for its chip's tooltip (S-e
 * slice 3; DB: "Spring 2025" on the chip, the range in the tooltip). The range
 * is the build window, 1 Apr to 31 May of the year: each composite is built
 * from that window's scenes (docs/imagery/S2_PRE_INGEST_SCREEN.md, the
 * <YYYY>-04-01_<YYYY>-05-31 folders); the database records only the start.
 * A date that is not a composite's (formatReferenceDate's rule) has no window.
 * @param {string|null|undefined} d  YYYY_04_01 or YYYY-04-01
 * @returns {string|null} "1 Apr – 31 May 2025"
 */
export function referenceWindow(d) {
  const m = /^(\d{4})[_-]04[_-]01$/.exec(d ?? "");
  return m ? `1 Apr – 31 May ${m[1]}` : null;
}
